// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import ThemeLibrary from '@app/features/theme/state/ThemeLibrary';
import {
	createThemeAssetReference,
	createThemeLocalFileReference,
	upsertThemeCssHeader,
} from '@app/features/theme/utils/ThemeCssUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Input, Textarea} from '@app/features/ui/components/form/Input';
import {Switch} from '@app/features/ui/components/form/Switch';
import {
	getCachedDesktopWindowBehaviorSettings,
	getDesktopWindowBehaviorSettings,
	relaunchDesktopApp,
	setDesktopWindowBehaviorSettings,
} from '@app/features/ui/utils/DesktopWindowBehaviorUtils';
import {getElectronAPI, isDesktop} from '@app/features/ui/utils/NativeUtils';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/ThemeTab.module.css';
import type {DesktopWindowBehaviorSettings} from '@app/types/electron.d';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	CopyIcon,
	DownloadSimpleIcon,
	FileCssIcon,
	FilePlusIcon,
	FolderOpenIcon,
	PencilSimpleLineIcon,
	TrashIcon,
	UploadSimpleIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {
	copyText,
	createThemeExportFileName,
	downloadTextFile,
	openQuickCssEditor,
	parseTagInput,
	readFileText,
} from './themeUtils';
export const ThemeLibraryManager: React.FC = observer(() => {
	const {t} = useLingui();
	const cssImportInputRef = useRef<HTMLInputElement | null>(null);
	const assetInputRef = useRef<HTMLInputElement | null>(null);
	const libraryImportInputRef = useRef<HTMLInputElement | null>(null);
	const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);
	const [themeName, setThemeName] = useState('');
	const [themeDescription, setThemeDescription] = useState('');
	const [themeAuthor, setThemeAuthor] = useState('');
	const [themeVersion, setThemeVersion] = useState('');
	const [themeTags, setThemeTags] = useState('');
	const [themeCss, setThemeCss] = useState('');
	const [desktopWindowBehavior, setDesktopWindowBehaviorState] = useState<DesktopWindowBehaviorSettings | null>(
		getCachedDesktopWindowBehaviorSettings(),
	);
	const [desktopWindowBehaviorBusy, setDesktopWindowBehaviorBusy] = useState(false);
	const selectedTheme = ThemeLibrary.themes.find((theme) => theme.id === selectedThemeId) ?? null;
	const hasDesktopFileAccess = isDesktop() && Boolean(getElectronAPI()?.pickThemeLocalFiles);
	useEffect(() => {
		void ThemeLibrary.init();
	}, []);
	useEffect(() => {
		if (!isDesktop()) return;
		void getDesktopWindowBehaviorSettings().then((settings) => setDesktopWindowBehaviorState(settings));
	}, []);
	useEffect(() => {
		if (selectedThemeId && ThemeLibrary.themes.some((theme) => theme.id === selectedThemeId)) {
			return;
		}
		setSelectedThemeId(ThemeLibrary.themes[0]?.id ?? null);
	}, [selectedThemeId, ThemeLibrary.themes.length]);
	useEffect(() => {
		if (!selectedTheme) {
			setThemeName('');
			setThemeDescription('');
			setThemeAuthor('');
			setThemeVersion('');
			setThemeTags('');
			setThemeCss('');
			return;
		}
		setThemeName(selectedTheme.name);
		setThemeDescription(selectedTheme.description);
		setThemeAuthor(selectedTheme.author);
		setThemeVersion(selectedTheme.version);
		setThemeTags(selectedTheme.tags.join(', '));
		setThemeCss(selectedTheme.css);
	}, [selectedTheme?.id, selectedTheme?.updatedAt]);
	const handleCssImportClick = useCallback(() => {
		cssImportInputRef.current?.click();
	}, []);
	const handleAssetUploadClick = useCallback(() => {
		assetInputRef.current?.click();
	}, []);
	const handleLibraryImportClick = useCallback(() => {
		libraryImportInputRef.current?.click();
	}, []);
	const handleImportCssFiles = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const files = [...(event.target.files ?? [])];
			event.target.value = '';
			if (files.length === 0) return;
			void ThemeLibrary.importCssFiles(files).then((themes) => {
				ToastCommands.success(t`Imported ${themes.length} theme files.`);
			});
		},
		[t],
	);
	const handleUploadAssets = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const files = [...(event.target.files ?? [])];
			event.target.value = '';
			if (files.length === 0) return;
			void ThemeLibrary.uploadAssets(files).then((assets) => {
				ToastCommands.success(t`Uploaded ${assets.length} theme assets.`);
			});
		},
		[t],
	);
	const handleImportLibrary = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			event.target.value = '';
			if (!file) return;
			void readFileText(file)
				.then((text) => ThemeLibrary.importExportPayload(JSON.parse(text)))
				.then((result) => {
					ToastCommands.success(t`Imported ${result.themes} themes and ${result.assets} assets.`);
				})
				.catch(() => ToastCommands.error(t`We couldn't import that theme library file.`));
		},
		[t],
	);
	const handleImportDesktopDirectory = useCallback(() => {
		void ThemeLibrary.importDesktopThemeDirectory().then((themes) => {
			if (themes.length > 0) {
				ToastCommands.success(t`Imported ${themes.length} theme files.`);
			}
		});
	}, [t]);
	const handleAddDesktopLocalFiles = useCallback(() => {
		void ThemeLibrary.addDesktopLocalFiles().then((files) => {
			if (files.length > 0) {
				ToastCommands.success(t`Added ${files.length} local file references.`);
			}
		});
	}, [t]);
	const handleExportLibrary = useCallback(() => {
		void ThemeLibrary.buildExportPayload().then((payload) => {
			downloadTextFile(JSON.stringify(payload, null, 2), 'fluxer-theme-library.json', 'application/json;charset=utf-8');
		});
	}, []);
	const handleExportSelectedTheme = useCallback(() => {
		if (!selectedTheme) return;
		downloadTextFile(selectedTheme.css, createThemeExportFileName(selectedTheme), 'text/css;charset=utf-8');
	}, [selectedTheme]);
	const handleDuplicateSelectedTheme = useCallback(() => {
		if (!selectedTheme) return;
		void ThemeLibrary.duplicateTheme(selectedTheme.id).then((theme) => {
			if (theme) {
				setSelectedThemeId(theme.id);
			}
		});
	}, [selectedTheme]);
	const handleSaveSelectedTheme = useCallback(() => {
		if (!selectedTheme) return;
		const metadata = {
			name: themeName.trim() || selectedTheme.name,
			description: themeDescription,
			author: themeAuthor,
			version: themeVersion,
			tags: parseTagInput(themeTags),
		};
		void ThemeLibrary.updateThemeDetails(selectedTheme.id, {
			...metadata,
			css: upsertThemeCssHeader(themeCss, metadata),
		}).then(() => ToastCommands.success(t`Theme saved.`));
	}, [selectedTheme, themeName, themeDescription, themeAuthor, themeVersion, themeTags, themeCss, t]);
	const handleDeleteSelectedTheme = useCallback(() => {
		if (!selectedTheme) return;
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={t`Delete Theme?`}
					description={t`This removes the theme from this device.`}
					primaryText={t`Delete Theme`}
					primaryVariant="danger-primary"
					secondaryText={t`Cancel`}
					onPrimary={() => ThemeLibrary.deleteTheme(selectedTheme.id)}
				/>
			)),
		);
	}, [selectedTheme, t]);
	const handleResetThemeData = useCallback(() => {
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={t`Reset Custom Theme Data?`}
					description={t`This clears Quick CSS, imported themes, uploaded assets, and local file references on this device.`}
					primaryText={t`Reset Data`}
					primaryVariant="danger-primary"
					secondaryText={t`Cancel`}
					onPrimary={() => {
						AccessibilityCommands.update({customThemeCss: null});
						void ThemeLibrary.resetLibrary();
					}}
				/>
			)),
		);
	}, [t]);
	const handleSyncCustomCssChange = useCallback(
		(value: boolean) => {
			if (!value) {
				Accessibility.updateSettings({customThemeCssSyncAcrossDevices: false});
				return;
			}
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={t`Sync Custom CSS?`}
						description={
							<Trans>
								Only the CSS text is synced across devices. Uploaded assets, desktop file references, fonts, and media
								stay on this device.
							</Trans>
						}
						primaryText={t`Sync CSS Text`}
						secondaryText={t`Cancel`}
						onPrimary={() => Accessibility.updateSettings({customThemeCssSyncAcrossDevices: true})}
					/>
				)),
			);
		},
		[t],
	);
	const handleAllowTransparencyChange = useCallback(
		(value: boolean) => {
			if (!desktopWindowBehavior) return;
			setDesktopWindowBehaviorBusy(true);
			void setDesktopWindowBehaviorSettings({allowTransparency: value})
				.then((nextSettings) => {
					if (nextSettings) {
						setDesktopWindowBehaviorState(nextSettings);
					}
					ModalCommands.push(
						modal(() => (
							<ConfirmModal
								title={t`Restart Fluxer?`}
								description={t`Desktop window transparency changes apply after restarting the app.`}
								primaryText={t`Restart Now`}
								secondaryText={t`Later`}
								onPrimary={async () => relaunchDesktopApp()}
							/>
						)),
					);
				})
				.finally(() => setDesktopWindowBehaviorBusy(false));
		},
		[desktopWindowBehavior, t],
	);
	const handleCopyAssetReference = useCallback(
		(reference: string) => {
			void copyText(reference)
				.then(() => ToastCommands.success(t`Reference copied.`))
				.catch(() => ToastCommands.error(t`We couldn't copy that reference.`));
		},
		[t],
	);
	return (
		<div className={styles.customThemeManager}>
			<div className={styles.managerHeader}>
				<div>
					<h4 className={styles.managerTitle}>
						<Trans>Custom Theme Library</Trans>
					</h4>
					<p className={styles.managerDescription}>
						<Trans>Manage Quick CSS, theme files, and referenced assets on this device.</Trans>
					</p>
				</div>
				<Button variant="primary" fitContent leftIcon={<PencilSimpleLineIcon size={18} />} onClick={openQuickCssEditor}>
					<Trans>Open Quick CSS Editor</Trans>
				</Button>
			</div>
			<div className={styles.managerControls}>
				<Switch
					label={t`Sync Custom CSS across devices`}
					description={t`CSS text only; local files and uploaded assets stay on this device.`}
					value={Accessibility.customThemeCssSyncAcrossDevices}
					onChange={handleSyncCustomCssChange}
				/>
				{isDesktop() && desktopWindowBehavior ? (
					<Switch
						label={t`Allow transparent desktop window`}
						description={t`Lets custom CSS reveal transparent window regions after restarting the desktop app.`}
						value={desktopWindowBehavior.allowTransparency}
						disabled={desktopWindowBehaviorBusy}
						onChange={handleAllowTransparencyChange}
					/>
				) : null}
			</div>
			<div className={styles.managerButtonGrid}>
				<Button variant="secondary" fitContent leftIcon={<FileCssIcon size={18} />} onClick={handleCssImportClick}>
					<Trans>Import CSS Files</Trans>
				</Button>
				{hasDesktopFileAccess ? (
					<Button
						variant="secondary"
						fitContent
						leftIcon={<FolderOpenIcon size={18} />}
						onClick={handleImportDesktopDirectory}
					>
						<Trans>Import Theme Directory</Trans>
					</Button>
				) : null}
				<Button
					variant="secondary"
					fitContent
					leftIcon={<UploadSimpleIcon size={18} />}
					onClick={handleAssetUploadClick}
				>
					<Trans>Upload Assets</Trans>
				</Button>
				{hasDesktopFileAccess ? (
					<Button
						variant="secondary"
						fitContent
						leftIcon={<FilePlusIcon size={18} />}
						onClick={handleAddDesktopLocalFiles}
					>
						<Trans>Add Local Files</Trans>
					</Button>
				) : null}
				<Button
					variant="secondary"
					fitContent
					leftIcon={<DownloadSimpleIcon size={18} />}
					onClick={handleExportLibrary}
				>
					<Trans>Export Library</Trans>
				</Button>
				<Button
					variant="secondary"
					fitContent
					leftIcon={<UploadSimpleIcon size={18} />}
					onClick={handleLibraryImportClick}
				>
					<Trans>Import Library</Trans>
				</Button>
				<Button variant="danger-secondary" fitContent leftIcon={<TrashIcon size={18} />} onClick={handleResetThemeData}>
					<Trans>Reset Custom Theme Data</Trans>
				</Button>
			</div>
			<input
				ref={cssImportInputRef}
				type="file"
				accept=".css,text/css,text/plain"
				multiple
				className={styles.hiddenInput}
				onChange={handleImportCssFiles}
				aria-hidden="true"
				tabIndex={-1}
			/>
			<input
				ref={assetInputRef}
				type="file"
				multiple
				className={styles.hiddenInput}
				onChange={handleUploadAssets}
				aria-hidden="true"
				tabIndex={-1}
			/>
			<input
				ref={libraryImportInputRef}
				type="file"
				accept=".json,application/json,text/plain"
				className={styles.hiddenInput}
				onChange={handleImportLibrary}
				aria-hidden="true"
				tabIndex={-1}
			/>
			<div className={styles.libraryGrid}>
				<div className={styles.libraryPanel}>
					<div className={styles.panelHeader}>
						<h5 className={styles.panelTitle}>
							<Trans>Themes</Trans>
						</h5>
					</div>
					{ThemeLibrary.themes.length > 0 ? (
						<div className={styles.themeList}>
							{ThemeLibrary.themes.map((theme) => (
								<div key={theme.id} className={styles.themeListItem}>
									<button
										type="button"
										className={styles.themeListButton}
										aria-pressed={selectedTheme?.id === theme.id}
										onClick={() => setSelectedThemeId(theme.id)}
									>
										<span className={styles.themeListName}>{theme.name}</span>
										<span className={styles.themeListMeta}>{theme.fileName}</span>
									</button>
									<Switch
										compact
										ariaLabel={t`Enable ${theme.name}`}
										value={ThemeLibrary.enabledThemeIds.includes(theme.id)}
										onChange={(value) => ThemeLibrary.setThemeEnabled(theme.id, value)}
									/>
								</div>
							))}
						</div>
					) : (
						<p className={styles.emptyText}>
							<Trans>No custom themes saved on this device.</Trans>
						</p>
					)}
				</div>
				<div className={styles.libraryPanel}>
					<div className={styles.panelHeader}>
						<h5 className={styles.panelTitle}>
							<Trans>Theme Editor</Trans>
						</h5>
						<div className={styles.panelActions}>
							<Button
								variant="secondary"
								compact
								fitContent
								leftIcon={<DownloadSimpleIcon size={16} />}
								disabled={!selectedTheme}
								onClick={handleExportSelectedTheme}
							>
								<Trans>Export</Trans>
							</Button>
							<Button
								variant="secondary"
								compact
								fitContent
								disabled={!selectedTheme}
								onClick={handleDuplicateSelectedTheme}
							>
								<Trans>Duplicate</Trans>
							</Button>
							<Button
								variant="danger-secondary"
								compact
								fitContent
								leftIcon={<TrashIcon size={16} />}
								disabled={!selectedTheme}
								onClick={handleDeleteSelectedTheme}
							>
								<Trans>Delete</Trans>
							</Button>
						</div>
					</div>
					{selectedTheme ? (
						<div className={styles.themeEditor}>
							<div className={styles.themeEditorFields}>
								<Input label={t`Name`} value={themeName} onChange={(event) => setThemeName(event.target.value)} />
								<Input label={t`Author`} value={themeAuthor} onChange={(event) => setThemeAuthor(event.target.value)} />
								<Input
									label={t`Version`}
									value={themeVersion}
									onChange={(event) => setThemeVersion(event.target.value)}
								/>
								<Input label={t`Tags`} value={themeTags} onChange={(event) => setThemeTags(event.target.value)} />
							</div>
							<Input
								label={t`Description`}
								value={themeDescription}
								onChange={(event) => setThemeDescription(event.target.value)}
							/>
							<Textarea
								label={t`CSS`}
								minRows={10}
								maxRows={18}
								value={themeCss}
								className={styles.themeCssEditor}
								spellCheck={false}
								onChange={(event) => setThemeCss(event.target.value)}
							/>
							<Button variant="primary" fitContent onClick={handleSaveSelectedTheme}>
								<Trans>Save Theme</Trans>
							</Button>
						</div>
					) : (
						<p className={styles.emptyText}>
							<Trans>Select a custom theme to edit it.</Trans>
						</p>
					)}
				</div>
			</div>
			<div className={styles.referenceGrid}>
				<div className={styles.libraryPanel}>
					<div className={styles.panelHeader}>
						<h5 className={styles.panelTitle}>
							<Trans>Uploaded Assets</Trans>
						</h5>
					</div>
					{ThemeLibrary.assets.length > 0 ? (
						<div className={styles.referenceList}>
							{ThemeLibrary.assets.map((asset) => {
								const reference = createThemeAssetReference(asset.name);
								return (
									<div key={asset.id} className={styles.referenceItem}>
										<div className={styles.referenceText}>
											<span className={styles.referenceName}>{asset.name}</span>
											<code className={styles.referenceCode}>{reference}</code>
										</div>
										<div className={styles.referenceActions}>
											<Button
												variant="secondary"
												square
												aria-label={t`Copy reference for ${asset.name}`}
												icon={<CopyIcon size={18} />}
												onClick={() => handleCopyAssetReference(reference)}
											/>
											<Button
												variant="danger-secondary"
												square
												aria-label={t`Delete ${asset.name}`}
												icon={<TrashIcon size={18} />}
												onClick={() => ThemeLibrary.deleteAsset(asset.id)}
											/>
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<p className={styles.emptyText}>
							<Trans>No uploaded assets saved on this device.</Trans>
						</p>
					)}
				</div>
				<div className={styles.libraryPanel}>
					<div className={styles.panelHeader}>
						<h5 className={styles.panelTitle}>
							<Trans>Local File References</Trans>
						</h5>
					</div>
					{ThemeLibrary.localFiles.length > 0 ? (
						<div className={styles.referenceList}>
							{ThemeLibrary.localFiles.map((file) => {
								const reference = createThemeLocalFileReference(file.path);
								return (
									<div key={file.id} className={styles.referenceItem}>
										<div className={styles.referenceText}>
											<span className={styles.referenceName}>{file.name}</span>
											<code className={styles.referenceCode}>{reference}</code>
										</div>
										<div className={styles.referenceActions}>
											<Button
												variant="secondary"
												square
												aria-label={t`Copy reference for ${file.name}`}
												icon={<CopyIcon size={18} />}
												onClick={() => handleCopyAssetReference(reference)}
											/>
											<Button
												variant="danger-secondary"
												square
												aria-label={t`Delete ${file.name}`}
												icon={<TrashIcon size={18} />}
												onClick={() => ThemeLibrary.deleteLocalFile(file.id)}
											/>
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<p className={styles.emptyText}>
							<Trans>No local files referenced on this device.</Trans>
						</p>
					)}
				</div>
			</div>
		</div>
	);
});
